import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { ScanLine, RefreshCw, AlertTriangle, CheckCircle2, X, Home, LogOut, PackagePlus, Store, Wallet, Clock, Package, FileSpreadsheet, Download } from "lucide-react";
import RequireAuth from "../components/RequireAuth";
import { authApi, logout } from "../lib/auth";
import { toast } from "sonner";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- helpers ----------
const fmtSyncAt = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};
const freshnessTone = (d) => {
  if (!d) return "bg-slate-100 text-slate-500 border-slate-300";
  const mins = (Date.now() - new Date(d).getTime()) / 60000;
  if (mins < 30) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (mins < 180) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-rose-100 text-rose-700 border-rose-300";
};
const fmtIST = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

function LabelScanContent({ user }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]); // last 20 scans
  const [syncing, setSyncing] = useState(false);
  const [srSyncing, setSrSyncing] = useState(false);
  const [srStatus, setSrStatus] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [stuck, setStuck] = useState({ orders: [], count: 0 });
  const [stuckDismissed, setStuckDismissed] = useState(false);
  const [dupChoice, setDupChoice] = useState(null); // {candidates, code}
  const [logOpen, setLogOpen] = useState(false);
  const [logRange, setLogRange] = useState(() => {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    return { from: today, to: today, scanner_email: "", user_id: "" };
  });
  const [logData, setLogData] = useState({ count: 0, rows: [], truncated: false });
  const [logLoading, setLogLoading] = useState(false);
  const inputRef = useRef(null);

  const brand = brands.find((b) => b.user_id === brandId);
  const isAllowed = user.role === "admin" || user.role === "label_scanner";
  const lowBalance = !!brand && brand.shipping_mode === "brand" && (brand.pending_liability || 0) > (brand.wallet_balance || 0);

  // ---------- loaders ----------
  const loadBrands = useCallback(async () => {
    try {
      const r = await authApi.get("/admin/brands");
      const list = (r.data || []).filter((b) => b.role === "brand");
      setBrands(list);
      // Deliberately do NOT auto-select — the scanner must pick a brand first
      // to prevent scanning against the wrong account.
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load brands"); }
  }, []);

  const loadSrStatus = useCallback(async () => {
    try { const r = await authApi.get("/admin/shiprocket/sync-status"); setSrStatus(r.data || null); }
    catch { /* silent */ }
  }, []);
  const loadUnassigned = useCallback(async () => {
    try { const r = await authApi.get("/admin/shiprocket/unassigned"); setUnassigned(r.data || []); }
    catch { /* silent */ }
  }, []);
  const loadStuck = useCallback(async () => {
    try { const r = await authApi.get("/admin/label-scan/stuck"); setStuck(r.data || { orders: [], count: 0 }); }
    catch { /* silent */ }
  }, []);

  useEffect(() => { if (isAllowed) { loadBrands(); loadSrStatus(); loadUnassigned(); loadStuck(); } }, [isAllowed, loadBrands, loadSrStatus, loadUnassigned, loadStuck]);

  // Sticky auto-focus on the scan input so barcode readers keep working scan-after-scan
  // without the operator ever needing to click back into the field. Re-focus when:
  //   • the selected brand changes, • busy state resolves after a scan, • a modal closes,
  //   • the window regains focus, • any click hits outside a real input/button/select.
  const focusScanInput = useCallback(() => {
    // defer to after react commits the state update so the element is mounted + enabled
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
  }, []);
  useEffect(() => { if (isAllowed) focusScanInput(); }, [brandId, busy, dupChoice, isAllowed, focusScanInput]);
  useEffect(() => { if (!logOpen && isAllowed) focusScanInput(); }, [logOpen, isAllowed, focusScanInput]);
  useEffect(() => { if (!stuckDismissed && stuck.count > 0) return; if (isAllowed) focusScanInput(); }, [stuckDismissed, stuck.count, isAllowed, focusScanInput]);
  useEffect(() => {
    const onWin = () => focusScanInput();
    window.addEventListener("focus", onWin);
    // Refocus on any click that isn't on another interactive element (so history rows,
    // labels, or the background never trap focus).
    const onClick = (e) => {
      const t = e.target;
      if (!t) return focusScanInput();
      const tag = t.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "BUTTON" || tag === "TEXTAREA" || tag === "A") return;
      if (t.closest && t.closest("input,select,button,textarea,a,[role=button]")) return;
      focusScanInput();
    };
    document.addEventListener("click", onClick);
    return () => { window.removeEventListener("focus", onWin); document.removeEventListener("click", onClick); };
  }, [focusScanInput]);

  // Sync freshness (Shopify: latest store sync across the currently-selected brand; falls back to global max)
  const lastShopifySync = useMemo(() => {
    const pool = brand ? [brand] : brands;
    const ts = pool.flatMap((b) => (b.stores || []).map((s) => s.last_sync_at)).filter(Boolean).map((d) => new Date(d).getTime());
    return ts.length ? new Date(Math.max(...ts)).toISOString() : null;
  }, [brand, brands]);
  const lastShiprocketSync = srStatus?.finished_at || null;

  // ---------- scan flow ----------
  const recordHistory = (rec) => setHistory((h) => [rec, ...h].slice(0, 20));
  const handleScanResponse = (v, data) => {
    const rec = { id: Date.now() + Math.random(), code: v, ...data };
    if (data.result === "label_printed") {
      recordHistory(rec);
      // If the scanner scanned an AWB (not the order id itself), surface the resolved order.
      const resolvedByAwb = data.awb && v.replace(/^#/, "").toLowerCase() === String(data.awb).toLowerCase();
      toast.success(resolvedByAwb
        ? `✓ AWB ${data.awb} → ${data.order_name} · label recorded`
        : `✓ ${data.order_name} · label recorded`);
    } else if (data.result === "duplicate") {
      recordHistory(rec);
      toast.warning(`Duplicate scan — ${data.order_name || v} already scanned by ${data.label_scanned_by_name || "team"}`);
    } else if (data.result === "duplicate_order_id") {
      // Do not record yet — user must choose
      setDupChoice({ code: v, candidates: data.candidates || [] });
    } else if (data.result === "cancelled") {
      recordHistory(rec);
      toast.error(`Cancelled order — do not print`);
    } else {
      recordHistory(rec);
      toast.error(`Not found: ${v}`);
    }
  };
  const doScan = async () => {
    const v = code.trim();
    if (!v || !brandId) return;
    setBusy(true);
    try {
      const r = await authApi.post("/admin/shipments/scan-label", { user_id: brandId, code: v });
      handleScanResponse(v, r.data);
      setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    finally { setBusy(false); focusScanInput(); }
  };
  const proceedForceScan = async (shipment_id) => {
    if (!dupChoice) return;
    setBusy(true);
    try {
      const r = await authApi.post("/admin/shipments/scan-label", { user_id: brandId, code: dupChoice.code, shipment_id });
      handleScanResponse(dupChoice.code, r.data);
      setDupChoice(null);
      setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    finally { setBusy(false); focusScanInput(); }
  };

  // ---------- sync buttons ----------
  const syncShopify = async () => {
    if (!brandId) return toast.error("Pick a brand");
    setSyncing(true);
    try {
      const r = await authApi.post("/admin/shipments/sync", { user_id: brandId });
      toast.success(`Shopify: ${r.data.new || 0} new order(s)`);
      loadBrands();
    } catch (e) { toast.error(e.response?.data?.detail || "Shopify sync failed"); }
    finally { setSyncing(false); }
  };
  const syncShiprocket = async () => {
    setSrSyncing(true);
    try {
      await authApi.post("/admin/shiprocket/sync");
      toast.info("Shiprocket sync started — this can take a minute…");
      // poll a few times
      let tries = 30;
      const poll = async () => {
        if (tries-- <= 0) { setSrSyncing(false); return; }
        try {
          const r = await authApi.get("/admin/shiprocket/sync-status");
          setSrStatus(r.data || null);
          if (r.data?.status === "done") {
            toast.success(`Shiprocket: ${r.data.archived_removed} archived removed · ${r.data.custom_unassigned} panel order(s) queued`);
            setSrSyncing(false); loadUnassigned(); return;
          }
          if (r.data?.status === "error") { toast.error(`Shiprocket sync error: ${r.data.error || "unknown"}`); setSrSyncing(false); return; }
        } catch { /* silent */ }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 5000);
    } catch (e) { toast.error(e.response?.data?.detail || "Shiprocket sync failed"); setSrSyncing(false); }
  };

  // ---------- unassigned Shiprocket -> brand ----------
  const assignShiprocket = async (soid) => {
    if (!brandId) return toast.error("Pick a brand first");
    if (!brand) return;
    if (!window.confirm(`Assign Shiprocket panel order ${soid} to ${brand.brand_name || brand.email}?`)) return;
    try {
      await authApi.post("/admin/shiprocket/assign", { shiprocket_order_id: soid, user_id: brandId });
      toast.success(`Assigned ${soid} → ${brand.brand_name || brand.email}`);
      loadUnassigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };

  // ---------- scan-log viewer ----------
  const loadScanLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      if (logRange.from) params.set("start", logRange.from);
      if (logRange.to) params.set("end", logRange.to);
      if (logRange.user_id) params.set("user_id", logRange.user_id);
      if (logRange.scanner_email) params.set("scanner_email", logRange.scanner_email);
      const r = await authApi.get(`/admin/label-scan/logs?${params.toString()}`);
      setLogData(r.data || { count: 0, rows: [], truncated: false });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load scan log"); }
    finally { setLogLoading(false); }
  }, [logRange]);
  const openLog = () => { setLogOpen(true); setTimeout(loadScanLog, 0); };
  const downloadLogXlsx = () => {
    const params = new URLSearchParams();
    if (logRange.from) params.set("start", logRange.from);
    if (logRange.to) params.set("end", logRange.to);
    if (logRange.user_id) params.set("user_id", logRange.user_id);
    if (logRange.scanner_email) params.set("scanner_email", logRange.scanner_email);
    // authApi carries session cookies via `withCredentials: true`; use a plain download link so
    // the browser attaches the same cookie automatically and streams the file to disk.
    const url = `${API_ROOT}/admin/label-scan/export?${params.toString()}`;
    window.open(url, "_blank");
  };

  if (!isAllowed) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white border-2 border-slate-950 p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-rose-500" />
          <h2 className="mt-3 font-extrabold text-lg uppercase">Access denied</h2>
          <p className="mt-2 text-sm text-slate-600">Your account isn't whitelisted for label scanning. Ask an admin to add you under Team.</p>
          <button onClick={logout} className="mt-4 text-xs font-bold uppercase text-slate-500 hover:text-slate-900">Sign out</button>
        </div>
      </div>
    );
  }

  const showStuckPopup = !stuckDismissed && stuck.count > 0;

  return (
    <div className={`min-h-screen ${lowBalance ? "bg-rose-50" : "bg-slate-100"}`} data-testid="label-scan-page">
      <header className="bg-slate-950 text-white border-b-2 border-[#FFD400]">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="text-slate-300 hover:text-white"><Home className="w-4 h-4" /></Link>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">/</span>
            <span className="font-extrabold uppercase text-sm flex items-center gap-1.5"><ScanLine className="w-4 h-4" /> Label scan</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-slate-400 hidden sm:inline">{user.email}</span>
            <button onClick={logout} data-testid="label-scan-logout" className="text-xs font-bold uppercase text-slate-300 hover:text-white"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-6 space-y-5">
        {/* Sync freshness pills */}
        <div className="flex flex-wrap items-center gap-2" data-testid="ls-sync-freshness">
          <span className={`inline-flex items-center gap-1.5 border-2 px-2.5 py-1 text-[11px] font-bold uppercase ${freshnessTone(lastShopifySync)}`} data-testid="ls-freshness-shopify" title={lastShopifySync ? "Last successful Shopify store sync" : "No Shopify sync yet"}>
            <Store className="w-3.5 h-3.5" /> Shopify · {fmtSyncAt(lastShopifySync)}
          </span>
          <span className={`inline-flex items-center gap-1.5 border-2 px-2.5 py-1 text-[11px] font-bold uppercase ${freshnessTone(lastShiprocketSync)}`} data-testid="ls-freshness-shiprocket" title={lastShiprocketSync ? "Last successful Shiprocket sync" : "No Shiprocket sync yet"}>
            <PackagePlus className="w-3.5 h-3.5" /> Shiprocket · {fmtSyncAt(lastShiprocketSync)}
          </span>
          {unassigned.length > 0 && (
            <span className="inline-flex items-center gap-1.5 border-2 border-amber-400 bg-amber-100 text-amber-800 px-2.5 py-1 text-[11px] font-bold uppercase" data-testid="ls-unassigned-count">
              <Package className="w-3.5 h-3.5" /> {unassigned.length} unassigned Shiprocket
            </span>
          )}
        </div>

        {/* Brand + sync bar */}
        <div className={`bg-white border-2 p-4 flex flex-wrap items-center gap-2 ${lowBalance ? "border-rose-600" : "border-slate-950"}`}>
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="label-scan-brand" className="grow min-w-[200px] border-2 border-slate-950 px-3 py-2 text-sm font-medium">
            <option value="">Select brand…</option>
            {brands.map((b) => <option key={b.user_id} value={b.user_id}>{b.brand_name ? `${b.brand_name} (${b.email})` : b.email} · ₹{b.wallet_balance ?? 0}</option>)}
          </select>
          <button onClick={syncShopify} disabled={syncing || !brandId} data-testid="label-scan-sync-shopify" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-[#FFD400] px-3 py-2 text-xs font-bold uppercase disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync Shopify
          </button>
          <button onClick={syncShiprocket} disabled={srSyncing} data-testid="label-scan-sync-shiprocket" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-[#FFD400] px-3 py-2 text-xs font-bold uppercase disabled:opacity-50">
            <PackagePlus className={`w-3.5 h-3.5 ${srSyncing ? "animate-pulse" : ""}`} /> Sync Shiprocket
          </button>
          <button onClick={openLog} data-testid="label-scan-log-btn" className="flex items-center gap-1.5 border-2 border-slate-950 bg-white hover:bg-emerald-100 px-3 py-2 text-xs font-bold uppercase">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Scan log
          </button>
        </div>

        {/* Low-balance warning */}
        {lowBalance && (
          <div className="border-4 border-rose-600 bg-rose-100 p-4 flex items-start gap-3" data-testid="low-balance-warning">
            <Wallet className="w-6 h-6 text-rose-700 shrink-0" />
            <div className="min-w-0">
              <p className="font-extrabold uppercase text-rose-800 text-sm">Insufficient wallet balance</p>
              <p className="text-xs text-rose-700 mt-1">
                <b>{brand.brand_name || brand.email}</b> has pending charges of <b>₹{(brand.pending_liability || 0).toLocaleString("en-IN")}</b> but a wallet balance of only <b>₹{(brand.wallet_balance || 0).toLocaleString("en-IN")}</b>. Ask the admin to top up before packing.
              </p>
            </div>
          </div>
        )}

        {/* Scan input */}
        <div className={`bg-white border-2 p-6 ${lowBalance ? "border-rose-600" : "border-slate-950"}`}>
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Scan or type order id</label>
          <div className="mt-2 flex gap-2">
            <input
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doScan()}
              data-testid="label-scan-input"
              placeholder="#10231 or scan barcode"
              className="grow border-2 border-slate-950 px-4 py-3 text-lg font-mono font-bold focus:outline-none"
              disabled={busy || !brandId}
              autoFocus
            />
            <button onClick={doScan} disabled={busy || !brandId || !code.trim()} data-testid="label-scan-submit" className="bg-slate-950 text-white font-bold uppercase px-5 py-3 border border-slate-950 hover:bg-slate-800 disabled:opacity-40">
              Record
            </button>
          </div>
          {brand && <p className="mt-2 text-[11px] font-mono text-slate-400">Scanning for <b>{brand.brand_name || brand.email}</b> · wallet ₹{(brand.wallet_balance || 0).toLocaleString("en-IN")}</p>}
        </div>

        {/* Unassigned Shiprocket panel orders (assign to selected brand) */}
        {unassigned.length > 0 && (
          <div className="bg-white border-2 border-slate-950" data-testid="ls-unassigned-panel">
            <div className="bg-slate-100 px-4 py-2 border-b-2 border-slate-950 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Shiprocket panel orders · needs a brand</span>
              <span className="text-[10px] font-mono text-slate-500">Selected brand receives the assignment</span>
            </div>
            <div className="divide-y divide-slate-200 max-h-72 overflow-auto">
              {unassigned.map((u) => (
                <div key={u.shiprocket_order_id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`ls-unassigned-row-${u.shiprocket_order_id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{u.order_name || `#${u.shiprocket_order_id}`}</p>
                    <p className="text-[11px] font-mono text-slate-500 truncate">SR #{u.shiprocket_order_id} · {Object.entries(u.sizes || {}).map(([k, v]) => `${k}×${v}`).join(" · ") || "—"}</p>
                  </div>
                  <button onClick={() => assignShiprocket(u.shiprocket_order_id)} disabled={!brandId} data-testid={`ls-assign-${u.shiprocket_order_id}`} className="text-[11px] font-bold uppercase border-2 border-slate-950 bg-white hover:bg-[#FFD400] px-3 py-1.5 disabled:opacity-40">
                    Assign
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        <div className="bg-white border-2 border-slate-950" data-testid="label-scan-history">
          <div className="bg-slate-100 px-4 py-2 border-b-2 border-slate-950 text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center justify-between">
            <span>Last {history.length} scan{history.length === 1 ? "" : "s"}</span>
            <span>{history.filter((h) => h.result === "label_printed").length} recorded</span>
          </div>
          {history.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No scans yet. Point your barcode reader at a label.</p>}
          <div className="divide-y divide-slate-200">
            {history.map((h) => {
              const good = h.result === "label_printed";
              const dupe = h.result === "duplicate";
              const bad = !good && !dupe;
              return (
                <div key={h.id} className={`px-4 py-3 flex items-center justify-between gap-3 ${good ? "" : dupe ? "bg-amber-50" : "bg-rose-50"}`} data-testid={`scan-row-${h.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {good ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : dupe ? <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" /> : <X className="w-5 h-5 text-rose-600 shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 truncate">{h.order_name || h.code}</p>
                      <p className="text-[11px] font-mono text-slate-500 truncate">
                        {good && <>Label recorded · {h.courier || "no courier"}{h.awb ? ` · AWB ${h.awb}` : ""}</>}
                        {dupe && <>Already scanned by {h.label_scanned_by_name || "team"}</>}
                        {bad && <>{h.result || "error"} — {h.code}</>}
                      </p>
                    </div>
                  </div>
                  {(good || dupe) && h.label_printed_at && (
                    <span className="text-[10px] font-mono text-slate-500 shrink-0">
                      {new Date(h.label_printed_at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* ---------- Modals ---------- */}
      {/* Duplicate order-id chooser: same order_name matches multiple shipments */}
      {dupChoice && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDupChoice(null)}>
          <div className="bg-white border-4 border-amber-500 max-w-xl w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="dup-order-modal">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-7 h-7 text-amber-600 shrink-0" />
              <div className="min-w-0">
                <h3 className="font-extrabold uppercase text-slate-950">Duplicate order id</h3>
                <p className="text-sm text-slate-600 mt-1">Order <b>{dupChoice.code}</b> matches {dupChoice.candidates.length} different shipments for this brand. This is either a duplicate label or two orders share the same number. Pick which one you're packing:</p>
              </div>
            </div>
            <div className="mt-4 divide-y divide-slate-200 border-2 border-slate-950">
              {dupChoice.candidates.map((c) => (
                <div key={c.shipment_id} className={`p-3 flex items-center justify-between gap-3 ${c.label_printed_at ? "bg-amber-50" : ""}`} data-testid={`dup-candidate-${c.shipment_id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{c.order_name} · <span className="font-mono text-xs text-slate-500">{c.source} #{c.shopify_order_id || c.shiprocket_order_id}</span></p>
                    <p className="text-[11px] font-mono text-slate-500 truncate">
                      {c.shop_name || c.store_domain || "—"} · {Object.entries(c.sizes || {}).map(([k, v]) => `${k}×${v}`).join(" · ") || "no sizes"} · ₹{Number(c.sale_amount || c.charge || 0).toLocaleString("en-IN")}
                      {c.awb && <> · <span className="font-bold text-slate-700">AWB {c.awb}</span></>}
                    </p>
                    {c.label_printed_at && (
                      <p className="text-[10px] font-bold uppercase text-amber-700 mt-1">Already scanned {fmtIST(c.label_printed_at)} by {c.label_scanned_by_name || "team"}</p>
                    )}
                  </div>
                  <button onClick={() => proceedForceScan(c.shipment_id)} disabled={busy || c.status === "cancelled"} data-testid={`dup-proceed-${c.shipment_id}`} className="text-[11px] font-bold uppercase border-2 border-slate-950 bg-white hover:bg-emerald-100 px-3 py-1.5 shrink-0 disabled:opacity-40">
                    {c.label_printed_at ? "Re-scan anyway" : "Different order — proceed"}
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setDupChoice(null)} data-testid="dup-cancel" className="text-xs font-bold uppercase border-2 border-slate-950 bg-white hover:bg-slate-100 px-4 py-2">Go back</button>
            </div>
          </div>
        </div>
      )}

      {/* Stuck orders popup — scanned but not shipped for >4 days */}
      {showStuckPopup && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" onClick={() => setStuckDismissed(true)}>
          <div className="bg-white border-4 border-rose-500 max-w-2xl w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="stuck-orders-modal">
            <div className="flex items-start gap-3">
              <Clock className="w-7 h-7 text-rose-600 shrink-0" />
              <div className="min-w-0 grow">
                <h3 className="font-extrabold uppercase text-slate-950">{stuck.count} confirmed order{stuck.count === 1 ? "" : "s"} stuck &gt; {stuck.cutoff_days || 4} days</h3>
                <p className="text-sm text-slate-600 mt-1">These orders were scanned but haven't shipped yet. Nudge the dispatch team.</p>
              </div>
            </div>
            <div className="mt-4 divide-y divide-slate-200 border-2 border-slate-950 max-h-80 overflow-auto">
              {stuck.orders.map((o) => (
                <div key={o.shipment_id} className="p-3 flex items-center justify-between gap-3" data-testid={`stuck-row-${o.shipment_id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{o.order_name} · <span className="font-mono text-xs text-slate-500">{o.brand_name || o.brand_email || "—"}</span></p>
                    <p className="text-[11px] font-mono text-slate-500 truncate">{o.source} · {o.courier || "no courier"} · {Object.entries(o.sizes || {}).map(([k, v]) => `${k}×${v}`).join(" · ") || "—"}</p>
                  </div>
                  <span className="text-[10px] font-bold uppercase text-rose-700 shrink-0">Scanned {fmtIST(o.label_printed_at)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setStuckDismissed(true)} data-testid="stuck-dismiss" className="text-xs font-bold uppercase border-2 border-slate-950 bg-white hover:bg-slate-100 px-4 py-2">Dismiss</button>
            </div>
          </div>
        </div>
      )}
      {/* Scan Log Modal — date-range list + Excel export */}
      {logOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setLogOpen(false)}>
          <div className="bg-white border-4 border-slate-950 max-w-5xl w-full max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="scan-log-modal">
            <div className="p-5 border-b-2 border-slate-950 flex flex-wrap items-center gap-3 shrink-0">
              <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
              <h3 className="font-extrabold uppercase text-slate-950 grow min-w-0">Label scan log</h3>
              <button onClick={() => setLogOpen(false)} data-testid="scan-log-close" className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 border-b-2 border-slate-200 grid grid-cols-2 md:grid-cols-5 gap-3 shrink-0">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">From</span>
                <input type="date" value={logRange.from} onChange={(e) => setLogRange((r) => ({ ...r, from: e.target.value }))} data-testid="scan-log-from" className="border-2 border-slate-950 px-2 py-1.5 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">To</span>
                <input type="date" value={logRange.to} onChange={(e) => setLogRange((r) => ({ ...r, to: e.target.value }))} data-testid="scan-log-to" className="border-2 border-slate-950 px-2 py-1.5 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Brand</span>
                <select value={logRange.user_id} onChange={(e) => setLogRange((r) => ({ ...r, user_id: e.target.value }))} data-testid="scan-log-brand" className="border-2 border-slate-950 px-2 py-1.5 text-sm">
                  <option value="">All brands</option>
                  {brands.map((b) => <option key={b.user_id} value={b.user_id}>{b.brand_name || b.email}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Scanner email</span>
                <input type="text" placeholder="any" value={logRange.scanner_email} onChange={(e) => setLogRange((r) => ({ ...r, scanner_email: e.target.value }))} data-testid="scan-log-scanner" className="border-2 border-slate-950 px-2 py-1.5 text-sm" />
              </label>
              <div className="flex items-end gap-2">
                <button onClick={loadScanLog} disabled={logLoading} data-testid="scan-log-apply" className="grow border-2 border-slate-950 bg-slate-950 text-white hover:bg-slate-800 px-3 py-1.5 text-xs font-bold uppercase disabled:opacity-40">
                  {logLoading ? "Loading…" : "Apply"}
                </button>
                <button onClick={downloadLogXlsx} data-testid="scan-log-download" title="Download filtered rows as Excel" className="border-2 border-emerald-600 bg-white text-emerald-600 hover:bg-emerald-600 hover:text-white px-3 py-1.5 text-xs font-bold uppercase inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> XLSX
                </button>
              </div>
            </div>
            <div className="p-5 overflow-auto grow">
              <div className="text-[11px] font-mono text-slate-500 mb-2" data-testid="scan-log-count">
                {logData.count} scan{logData.count === 1 ? "" : "s"}
                {logData.truncated && ` — showing first ${logData.rows.length} (refine the range to see more)`}
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100 border-2 border-slate-950 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Time (IST)</th>
                    <th className="text-left px-3 py-2">Order ID</th>
                    <th className="text-left px-3 py-2">Brand</th>
                    <th className="text-left px-3 py-2">Scanned by</th>
                    <th className="text-left px-3 py-2">Courier</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logData.rows.length === 0 && !logLoading && (
                    <tr><td colSpan={7} className="p-6 text-center text-sm text-slate-400">No scans in this range.</td></tr>
                  )}
                  {logData.rows.map((r) => {
                    const dt = r.label_printed_at ? new Date(r.label_printed_at) : null;
                    const date = dt ? dt.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "—";
                    const time = dt ? dt.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—";
                    return (
                      <tr key={r.shipment_id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`scan-log-row-${r.shipment_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{date}</td>
                        <td className="px-3 py-2 font-mono text-xs">{time}</td>
                        <td className="px-3 py-2 font-bold">{r.order_name || "—"}</td>
                        <td className="px-3 py-2 text-xs">{r.brand_name || r.brand_email || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.label_scanned_by_email || "—"}</td>
                        <td className="px-3 py-2 text-xs">{r.courier || "—"}</td>
                        <td className="px-3 py-2 text-[10px] font-bold uppercase">{r.status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LabelScan() {
  return (
    <RequireAuth redirectTarget="/label-scan">
      {(user) => <LabelScanContent user={user} />}
    </RequireAuth>
  );
}
